// client/src/pages/reveal/SoulReveal.tsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Progress,
  Image,
  useToast,
  Center,
  Spinner,
  Grid,
  GridItem,
  Badge,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Archetype, archetypeDescriptions } from '../../data/quizData';
import { getDominantArchetype, getSecondaryArchetype } from '../../utils/scoring';
import { GlossyBox } from "../../components/GlossyBox";

const MotionDiv = motion.div;
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5043/api';

interface UserResults {
  userId: string;
  photoUrl: string;
  scores: Array<{
    archetype: Archetype;
    value: number;
  }>;
  createdAt: string;
}

export default function SoulReveal() {
  const { userId } = useParams<{ userId: string }>();
  const [results, setResults] = useState<UserResults | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [chartData, setChartData] = useState<any[]>([]);
  const navigate = useNavigate();
  const toast = useToast();

  const gridLayout = useBreakpointValue({
    base: { template: '1fr', columns: 1 },
    md: { template: 'repeat(2, 1fr)', columns: 2 },
    lg: { template: 'repeat(3, 1fr)', columns: 3 },
  });

  const getColorForArchetype = (archetype: Archetype) => {
    const colors = {
      Vishnu: '#7D3C98',     // Deep purple for leadership and courage
      Lakshmi: '#3498DB',    // Blue for wisdom and knowledge
      Ketu: '#E74C3C',       // Red for grounding and responsibility
      Rahu: '#F1C40F',       // Yellow for materialistic nature
      Alakshmi: '#9B59B6',   // Purple for inner strength
      Eris: '#FF6B6B'        // Pink for playful and mischievous nature
    };
    return colors[archetype] || '#666';
  };

  useEffect(() => {
    if (!userId) {
      navigate('/play');
      return;
    }

    const fetchResults = async () => {
      try {
        const response = await axios.get(`${API_URL}/user/${userId}/results`);
        setResults(response.data);
        
        const data = response.data.scores.map((score: { archetype: Archetype; value: number; }) => ({
          name: score.archetype,
          value: score.value,
          color: getColorForArchetype(score.archetype)
        }));
        setChartData(data);
      } catch (error) {
        console.error('Error fetching results:', error);
        toast({
          title: 'Error',
          description: 'Failed to load your results. Please try again.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        navigate('/play');
      } finally {
        setIsLoading(false);
      }
    };

    fetchResults();
  }, [userId, navigate, toast]);

  if (isLoading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" color="purple.400" />
      </Center>
    );
  }

  if (!results || !chartData.length) {
    return null;
  }

  const dominantArchetype = getDominantArchetype(results.scores);
  const secondaryArchetype = getSecondaryArchetype(results.scores);

  const scoreRanges = [
    { min: 0, max: 20, label: 'Low Influence', color: 'gray' },
    { min: 21, max: 40, label: 'Minor Influence', color: 'yellow' },
    { min: 41, max: 60, label: 'Moderate Influence', color: 'blue' },
    { min: 61, max: 80, label: 'Strong Influence', color: 'purple' },
    { min: 81, max: 100, label: 'Dominant Influence', color: 'pink' }
  ];

  // Glossy card style configuration
  const glossyCardStyle = {
    bg: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(12px) saturate(160%)',
    borderRadius: '2xl',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    border: '1px solid',
    borderColor: 'rgba(255, 255, 255, 0.2)',
    position: 'relative',
    overflow: 'hidden',
    _before: {
      content: '""',
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 60%)',
      zIndex: 0
    }
  };

  return (
    <Container maxW="container.lg" py={8} position="relative" zIndex={1}>
      <VStack spacing={8} align="stretch">
        {/* Header */}
        <MotionDiv
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          style={{ textAlign: 'center' }}
        >
          <Heading
            as="h1"
            size={{ base: 'xl', md: '2xl' }}
            // bgGradient="linear(to-r, purple.400, pink.400)"
        bgGradient="linear(to-r, purple.300, pink.300)"
            bgClip="text"
            mb={2}
            // textShadow="0 2px 4px rgba(0,0,0,0.2)"
          >
            Your Soul Mirror
          </Heading>
          <Text fontSize={{ base: 'md', md: 'xl' }} color="white" fontWeight="medium">
            Discover the archetypes that shape your essence
          </Text>
        </MotionDiv>

        {/* Main Content Grid */}
        <Grid 
          templateColumns={gridLayout?.template || '1fr'} 
          gap={6}
          mt={8}
        >
          {/* Photo Card */}
          {/* Photo Card */}
<GridItem colSpan={1}>
  <MotionDiv
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5, delay: 0.2 }}
  >
    <GlossyBox p={6} position="relative" overflow="hidden">
      {/* Background Image */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        zIndex={0}
        opacity={1}
        backgroundImage="url('/images/SoulBackground.png')" // Replace with your image path
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundRepeat="no-repeat"
        filter="blur(1px)"
      />
      
      {/* Overlay with "Your Reflection" text */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        zIndex={1}
        display="flex"
        alignItems="center"
        justifyContent="center"
        pointerEvents="none"
      >
        {/* <Text
          fontSize={{ base: '4xl', md: '5xl' }}
          fontWeight="bold"
          color="whiteAlpha.200"
          textTransform="uppercase"
          letterSpacing="wider"
          transform="rotate(-15deg)"
          userSelect="none"
        >
          Your Reflection
        </Text> */}
      </Box>

      {/* User Avatar */}
      <Box position="relative" zIndex={2}>
        <Image
          src={results.photoUrl}
          // alt="Your reflection"
          borderRadius="lg"
          w="full"
          h="auto"
          objectFit="cover"
          aspectRatio="1/1"
          border="2px solid"
          borderColor="rgba(255, 255, 255, 0.3)"
          boxShadow="0 8px 32px rgba(0, 0, 0, 0.3)"
        />
      </Box>

      {/* Badge */}
      <VStack mt={4} align="center" position="relative" zIndex={2}>
        <Badge 
          bg="rgba(0, 0, 0, 0.6)"
          color="white"
          fontSize="md" 
          px={3} 
          py={1}
          borderRadius="full"
          backdropFilter="blur(4px)"
          boxShadow="inset 0 1px 2px rgba(255,255,255,0.2)"
        >
          Your Reflection
        </Badge>
      </VStack>
    </GlossyBox>
  </MotionDiv>
</GridItem>

          <GridItem colSpan={gridLayout?.columns === 3 ? 2 : 1}>
            <MotionDiv
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              {/* <Box
                {...glossyCardStyle}
                p={6}
                h="full"
              > */}
              <GlossyBox p={6}>

                <VStack spacing={4} align="stretch" position="relative">
                  <Heading as="h2" size="lg" color="white" fontWeight="bold">
                    Your Primary Archetype: <Text as="span" color="purple.100">{dominantArchetype}</Text>
                  </Heading>
                  <Text fontSize="lg" color="whiteAlpha.900" fontWeight="medium">
                    {archetypeDescriptions[dominantArchetype]}
                  </Text>
                  <Box pt={4} mt={4} borderTopWidth="1px" borderColor="rgba(255, 255, 255, 0.2)">
                    <Heading as="h3" size="md" color="white" fontWeight="bold">
                      Secondary Influence: <Text as="span" color="purple.100">{secondaryArchetype}</Text>
                    </Heading>
                    <Text fontSize="md" color="whiteAlpha.900" fontWeight="medium">
                      {archetypeDescriptions[secondaryArchetype]}
                    </Text>
                  </Box>
                </VStack>
              </GlossyBox>
              {/* </Box> */}
            </MotionDiv>
          </GridItem>
        </Grid>

        {/* Analysis Grid */}
        <Grid 
          templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} 
          gap={6}
          mt={6}
        >
          {/* Influence Gauge Card */}
          <GridItem>
            <MotionDiv
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              {/* <Box 
                {...glossyCardStyle}
                p={6}
              > */}<GlossyBox p={6}>

                <Heading size="md" mb={4} color="white" fontWeight="bold">Archetype Influence</Heading>
                <VStack spacing={4} align="stretch">
                  {chartData.map((data) => (
                    <Box key={data.name} position="relative">
                      <HStack justify="space-between" mb={1}>
                        <Text fontWeight="bold" color="white">{data.name}</Text>
                        <Badge 
                          bg="rgba(0, 0, 0, 0.6)"
                          color="white"
                          px={2} 
                          py={1}
                          borderRadius="md"
                          backdropFilter="blur(4px)"
                          boxShadow="inset 0 1px 2px rgba(255,255,255,0.2)"
                        >
                          {data.value}%
                        </Badge>
                      </HStack>
                      <Progress
                        value={data.value}
                        size="sm"
                        colorScheme={data.name === dominantArchetype ? 'pink' : 'purple'}
                        borderRadius="full"
                        bg="rgba(255, 255, 255, 0.3)"
                        height="8px"
                      />
                    </Box>
                  ))}
                </VStack>
              </GlossyBox>
              {/* </Box> */}
            </MotionDiv>
          </GridItem>

          {/* Score Analysis Card */}
          <GridItem>
            <MotionDiv
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.7 }}
            >
              {/* <Box 
                {...glossyCardStyle}
                p={6}
              > */}
              <GlossyBox p={6}>

                <Heading size="md" mb={4} color="white" fontWeight="bold">Strength Analysis</Heading>
                <VStack spacing={6} align="stretch">
                  {chartData.map((data) => {
                    const range = scoreRanges.find(
                      r => data.value >= r.min && data.value <= r.max
                    );
                    return (
                      <Box key={data.name} position="relative">
                        <HStack justify="space-between" mb={1}>
                          <Text fontWeight="bold" color="white">{data.name}</Text>
                          <HStack spacing={2}>
                            <Badge 
                              bg={`${range?.color}.600`}
                              color="white"
                              px={2}
                              py={1}
                              borderRadius="md"
                              backdropFilter="blur(4px)"
                              boxShadow="inset 0 1px 2px rgba(255,255,255,0.2)"
                            >
                              {range?.label}
                            </Badge>
                            <Text color="white" fontWeight="medium">{data.value}%</Text>
                          </HStack>
                        </HStack>
                        <Progress
                          value={data.value}
                          size="sm"
                          colorScheme={range?.color}
                          borderRadius="full"
                          bg="rgba(255, 255, 255, 0.3)"
                          height="8px"
                        />
                      </Box>
                    );
                  })}
                </VStack>
              </GlossyBox>
              {/* </Box> */}
            </MotionDiv>
          </GridItem>
        </Grid>

        {/* Archetype Composition */}
        <MotionDiv
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          {/* <Box 
            {...glossyCardStyle}
            p={6}
            mt={6}
          > */}

          <GlossyBox p={6}>

            <Heading size="md" mb={6} color="white" fontWeight="bold">Your Archetype Composition</Heading>
            <Grid 
              templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} 
              gap={6}
            >
              {chartData.map((data) => (
                <GridItem key={data.name}>
                  <Box 
                    p={4}
                    bg={`${data.color}30`}
                    backdropFilter="blur(8px)"
                    borderRadius="lg"
                    borderLeft="4px solid"
                    borderColor={data.color}
                    boxShadow="0 4px 20px rgba(0, 0, 0, 0.2)"
                    position="relative"
                    overflow="hidden"
                    _before={{
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 60%)',
                      zIndex: 0
                    }}
                  >
                    <Heading size="sm" mb={2} color="white" fontWeight="bold">
                      {data.name} <Text as="span" color="whiteAlpha.800">({data.value}%)</Text>
                    </Heading>
                    <Text fontSize="sm" color="whiteAlpha.900" fontWeight="medium">
                      {archetypeDescriptions[data.name as Archetype]}
                    </Text>
                  </Box>
                </GridItem>
              ))}
            </Grid>
          </GlossyBox>

          {/* </Box> */}
        </MotionDiv>
      </VStack>
    </Container>
  );
}
